import { Clock } from 'lucide-react';
import { useLetterStatusLogs } from '../hooks/useOpp';
import { formatDistanceToNow } from 'date-fns';
import { useCopy } from '../copy';
import { SemanticState } from './semantic/SemanticState';

interface LetterStatusTrackerProps {
  letterId: string;
}

const STATUS_CONFIG: Record<string, { color: string; background: string; dot: string }> = {
  draft:      { color: 'text-[hsl(var(--opp-status-draft-fg))]', background: 'bg-[hsl(var(--opp-status-draft-bg))]', dot: 'bg-[hsl(var(--opp-status-draft-fg))]' },
  generated:  { color: 'text-[hsl(var(--opp-status-generated-fg))]', background: 'bg-[hsl(var(--opp-status-generated-bg))]', dot: 'bg-[hsl(var(--opp-status-generated-fg))]' },
  collected:  { color: 'text-[hsl(var(--opp-status-collected-fg))]', background: 'bg-[hsl(var(--opp-status-collected-bg))]', dot: 'bg-[hsl(var(--opp-status-collected-fg))]' },
  in_transit: { color: 'text-[hsl(var(--opp-status-transit-fg))]', background: 'bg-[hsl(var(--opp-status-transit-bg))]', dot: 'bg-[hsl(var(--opp-status-transit-fg))]' },
  delivered:  { color: 'text-[hsl(var(--opp-status-delivered-fg))]', background: 'bg-[hsl(var(--opp-status-delivered-bg))]', dot: 'bg-[hsl(var(--opp-status-delivered-fg))]' },
  read:       { color: 'text-[hsl(var(--opp-status-read-fg))]', background: 'bg-[hsl(var(--opp-status-read-bg))]', dot: 'bg-[hsl(var(--opp-status-read-fg))]' },
};

export default function LetterStatusTracker({ letterId }: LetterStatusTrackerProps) {
  const { data: logs, isLoading, isError } = useLetterStatusLogs(letterId);
  const { t } = useCopy();

  if (isLoading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <div className="h-3 w-3 rounded-full bg-[hsl(var(--opp-accent-soft))] mt-0.5 shrink-0" />
            <div className="flex-1 space-y-1">
              <div className="h-3 w-20 rounded bg-[hsl(var(--opp-accent-soft))]" />
              <div className="h-2.5 w-32 rounded bg-[hsl(var(--opp-accent-soft))]" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return <SemanticState compact kind="error" illustration="state-error-network" title="状态加载失败" description="请刷新后重试。" />;
  }

  if (!logs || logs.length === 0) {
    return <SemanticState compact illustration="empty-comments" kind="empty" title="暂无状态记录" />;
  }

  // Most recent first
  const sorted = [...logs].reverse();

  return (
    <ol aria-label="信件状态时间线" className="space-y-0">
      {sorted.map((log, index) => {
        const cfg = STATUS_CONFIG[log.status] ?? {
          color: 'text-[hsl(var(--opp-accent-strong))]',
          background: 'bg-[hsl(var(--opp-accent-soft))]',
          dot: 'bg-[hsl(var(--opp-accent-soft))]',
        };
        const isLast = index === sorted.length - 1;
        const date = log.created_at
          ? formatDistanceToNow(new Date(log.created_at), { addSuffix: true })
          : '';
        const statusLabel = t(`letters.status.${log.status}` as never) || log.status;

        return (
          <li key={log.id} className="flex gap-3">
            {/* Timeline spine */}
            <div className="flex flex-col items-center">
              <div aria-hidden="true" className={`h-3 w-3 rounded-full shrink-0 mt-0.5 ${cfg.dot}`} />
              {!isLast && <div aria-hidden="true" className="w-px flex-1 bg-[hsl(var(--opp-accent-soft))] my-1" />}
            </div>

            {/* Content */}
            <div className={`pb-4 ${isLast ? '' : ''}`}>
              <p className={`inline-flex rounded-full px-2 py-0.5 text-sm font-semibold ${cfg.background} ${cfg.color}`}>{statusLabel}</p>
              <div className="flex items-center gap-1 mt-0.5 text-label text-[hsl(var(--opp-muted))]">
                <Clock aria-hidden="true" className="h-3 w-3" />
                <time dateTime={log.created_at ?? undefined}>{date}</time>
              </div>
              {log.note && (
                <p className="mt-1 text-xs text-[hsl(var(--opp-accent-strong))]">{log.note}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
